import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { ExamineeService } from 'src/app/services/examinee.service';
import { InviteExamineeComponent } from 'src/app/shared/components/invite-examinee/invite-examinee.component';
import { Examinee } from 'src/app/shared/models/examinee.model';

@Component({
    selector: 'app-project-examinee',
    templateUrl: './project-examinee.component.html',
    styleUrl: './project-examinee.component.scss',
    standalone: false
})
export class ProjectExamineeComponent {
	@ViewChild('inviteExamineeModal') inviteExamineeModal!: InviteExamineeComponent;
	@ViewChild('reInviteExamineeModal') reInviteExamineeModal!: InviteExamineeComponent;
	@ViewChild('removeExamineeModal') removeExamineeModal!: TemplateRef<any>;
	@ViewChild('deleteExamineeModal') deleteExamineeModal!: TemplateRef<any>;
	@ViewChild('successModal') successModal!: TemplateRef<any>;

	@Input() currentProject!: any;

	private refreshSubscription!: Subscription;
	private searchSubject = new Subject<string>();
	removeExamineeModalRef: any;

	submitted = false;
	spinner = false;
	success = false;
	successMessage = '';
	message = '';

	currentPage: number = 1;
  pageSize: number = 8;
  totalExaminees: number = 0;
  searchText: string = '';
  sortColumn: string = 'createdAt';
  sortDirection: string = 'desc';
	startIndex: number = 0;
  endIndex: number = 0;

	loading = true;
	isAllow = false;
	isEditor = false;
	isAnalyst = false;
	isOwner = false;

	examinees: Examinee[] = [];
	current_client: any = null;
	
	deleteModalRef: any;
	selectedExaminee!: Examinee;
	colspan = 0;

	allowedRoles = ['ANALYST', 'OWNER', 'EDITOR'];

	constructor(private examineeService: ExamineeService, private authService: AuthService, private modalService: NgbModal) {}

	ngOnInit(): void {
		this.current_client = this.authService.currentClientValue;

		this.isEditor = this.authService.currentClientValue.roles.includes('EDITOR');
		this.isAnalyst = this.authService.currentClientValue.roles.includes('ANALYST');
		this.isOwner = this.authService.currentClientValue.roles.includes('OWNER');
		this.isAllow = this.authService.currentClientValue.roles.some((role: string) => this.allowedRoles.includes(role));
		
		this.colspan = this.currentProject ? this.isAllow ? 7 : 6 : this.isAllow ? 8 : 7;

		this.getExaminees();

		this.refreshSubscription = this.examineeService.getRefreshObservable().subscribe(() => {
      this.getExaminees();
    });

    // Subscribe to search input changes with debounce
    this.searchSubject.pipe(
      debounceTime(300), // Adjust debounce time as needed
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 1; // Reset to first page when search text changes
      this.getExaminees();
    });
	}

	ngOnDestroy() {
    this.refreshSubscription.unsubscribe();
  }

	openModal(mode: string, examinee?: Examinee) {
		switch (mode) {
			case 'invite':
				this.inviteExamineeModal.openModal(this.currentProject.id);
				break;
			case 'reinvite':
				this.inviteExamineeModal.openModal(this.currentProject.id, examinee?.questionnaire, examinee);
				break;
			case 'delete':
				if (examinee) {
					this.selectedExaminee = examinee;
					this.deleteModalRef = this.modalService.open(this.deleteExamineeModal, { size: 'l', backdrop: 'static', keyboard : false });
				}
				break;
		}
	}

	getStatusColor(examinee: Examinee) {
		if (examinee.completed_at) {
			return 'success';
		}

		if (examinee.answered_at) {
			return 'info';
		}

		if (examinee.started_at) {
			return 'info';
		}

		if (examinee.failed_at) {
			return 'danger';
		}

		if (this.isExpired(examinee.expired_at)) {
			return 'danger';
		}
		
		return 'warning';
	}

	getStatus(examinee: Examinee) {
		if (examinee.completed_at) {
			return 'COMPLETED';
		}

		if (examinee.answered_at) {
			return 'PROCESSING';
		}

		if (examinee.started_at) {
			return 'STARTED';
		}

		if (examinee.failed_at) {
			return 'FAILED';
		}

		if (this.isExpired(examinee.expired_at)) {
			return 'EXPIRED';
		}
		
		return 'INVITED';
	}

	isExpired(expired_at: string): boolean {
    const today = new Date();
    const expiredAt = new Date(expired_at);

    return expiredAt.getTime() < today.getTime(); 
  }

	getExaminees() {
		this.loading = true;
    const params = {
      page: this.currentPage.toString(),
      pageSize: this.pageSize.toString(),
      searchText: this.searchText,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection
    };

		if (this.currentProject) {
			this.examineeService.getProjectExaminees(this.currentProject.id, params).subscribe({
				next: (response: any) => {
					this.examinees = response.examinees;
					this.totalExaminees = response.totalExaminees;
					this.calculateIndexes();
					this.loading = false;
				},
				error: (err) => {
					this.authService.toastErrorMessage(err.message ? err.message : err, 'Project Examinees');
				}
			});
		} else {
			this.examineeService.getExaminees(params).subscribe({
				next: (response: any) => {
					this.examinees = response.examinees;
					this.totalExaminees = response.totalExaminees;
					this.calculateIndexes();
					this.loading = false;
				},
				error: (err) => {
					this.authService.toastErrorMessage(err.message ? err.message : err, 'Project Examinees');
				}
			});
		}
  }

	calculateIndexes() {
    this.startIndex = (this.currentPage - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.currentPage * this.pageSize, this.totalExaminees);
  }

	onSearch() {
    this.searchSubject.next(this.searchText);
  }

	onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.getExaminees();
  }

	refreshExaminees() {
		this.currentPage = 1;
		this.searchText = '';
		this.getExaminees();
	}

	closeModal() {
		if (this.success) {
			this.refreshExaminees();
			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}

	deleteExaminee() {
		this.submitted = true;
		this.spinner = true;

		this.examineeService.deleteExaminee(this.selectedExaminee.id).subscribe({
			next: () => {
				this.submitted = false;
				this.spinner = false;
				this.success = true;
				this.successMessage = 'Examinee assessment has been deleted successfully.';
				this.deleteModalRef.close();
				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.submitted = false;
				this.spinner = false;
				this.success = false;
				this.successMessage = 'Examinee assessment can not be deleted!';
				this.message = err.message ? err.message : err;

				this.deleteModalRef.close();
				this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
			}
		});
	}

}
